import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Upload,
  Save,
  Trash2,
  Edit2,
  FileSpreadsheet,
  X,
  AlertCircle,
  Loader2,
  ChevronDown,
  Search
} from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  fetchPFInterestEntries,
  createPFInterestEntry,
  updatePFInterestEntry,
  deletePFInterestEntry,
  bulkUploadPFInterest
} from '../../services/pfInterestApi';
import { useAuth } from '../../contexts/AuthContext';

const emptyRow = {
  bank_name: '',
  date_of_investment: '',
  fd_no: '',
  amount_of_fd: '',
  rate_of_interest: '',
  duration: '',
  date_of_maturity: '',
  month: '',
  maturity_value: '',
  remark: ''
};

/**
 * Generate a dynamic list of financial years from 2023-24 to current + 2
 */
function getFinancialYears() {
  const currentYear = new Date().getFullYear();
  const startYear = 2025;
  const years = [];
  for (let y = startYear; y <= currentYear + 2; y++) {
    years.push(`${y}-${(y + 1).toString().slice(-2)}`);
  }
  return years;
}

/**
 * Get current financial year string (April - March).
 * If current month >= April, FY = currentYear - nextYear, else FY = prevYear - currentYear.
 */
function getCurrentFY() {
  const now = new Date();
  const month = now.getMonth(); // 0-indexed (0=Jan, 3=Apr)
  const year = now.getFullYear();
  if (month >= 3) {
    // April onwards
    return `${year}-${(year + 1).toString().slice(-2)}`;
  } else {
    return `${year - 1}-${year.toString().slice(-2)}`;
  }
}

export default function PFInterest({ isEmbedded = false }) {
  const { hasPermission } = useAuth();
  const canView = hasPermission('pf:view');
  const canAdd = hasPermission('pf:add');

  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({ total_amount_of_fd: 0, total_maturity_value: 0, total_interest_amount: 0, total_interest_receivable: 0, total_interest_received: 0, entry_count: 0 });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [loading, setLoading] = useState(true);

  // Bulk upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Financial Year
  const [financialYear, setFinancialYear] = useState(getCurrentFY);
  const fyString = `FY ${financialYear}`;

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [bankFilter, setBankFilter] = useState('');
  const [investDateFrom, setInvestDateFrom] = useState('');
  const [investDateTo, setInvestDateTo] = useState('');
  const [maturityDateFrom, setMaturityDateFrom] = useState('');
  const [maturityDateTo, setMaturityDateTo] = useState('');

  // Reset filters
  const handleResetFilters = () => {
    setSearchQuery('');
    setBankFilter('');
    setInvestDateFrom('');
    setInvestDateTo('');
    setMaturityDateFrom('');
    setMaturityDateTo('');
  };

  // Get unique banks from loaded data for select options
  const uniqueBanks = useMemo(() => {
    const banks = entries
      .map(e => e.bank_name)
      .filter(name => typeof name === 'string' && name.trim() !== '');
    return Array.from(new Set(banks)).sort();
  }, [entries]);

  // Client-side filtering logic
  const filteredEntries = useMemo(() => {
    return entries.filter(entry => {
      // 1. Search Query (Bank Name, FD number)
      if (searchQuery) {
        const query = searchQuery.toLowerCase().trim();
        const bankName = (entry.bank_name || '').toLowerCase();
        const fdNo = (entry.fd_no || '').toLowerCase();
        if (!bankName.includes(query) && !fdNo.includes(query)) {
          return false;
        }
      }

      // 2. Bank Filter
      if (bankFilter && entry.bank_name !== bankFilter) {
        return false;
      }

      // Helper function to safely parse entry dates in various formats
      const parseDateSafe = (dateStr) => {
        if (!dateStr) return null;
        const s = String(dateStr).trim().split(' ')[0];
        if (!s) return null;

        // YYYY-MM-DD
        if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(s)) {
          const parts = s.split('-');
          const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
          return isNaN(d.getTime()) ? null : d;
        }

        // DD-MM-YYYY, DD/MM/YYYY, DD.MM.YYYY, DD-MM-YY, etc.
        const match = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/);
        if (match) {
          let day = parseInt(match[1], 10);
          let month = parseInt(match[2], 10) - 1;
          let year = parseInt(match[3], 10);
          if (year < 100) {
            year += year >= 50 ? 1900 : 2000;
          }
          const d = new Date(year, month, day);
          return isNaN(d.getTime()) ? null : d;
        }

        const d = new Date(s);
        return isNaN(d.getTime()) ? null : d;
      };

      // 3. Investment Date Filter (From - To)
      if (investDateFrom || investDateTo) {
        const entryDate = parseDateSafe(entry.date_of_investment);
        if (!entryDate) return false;
        if (investDateFrom) {
          const fromDate = parseDateSafe(investDateFrom);
          if (fromDate && entryDate < fromDate) return false;
        }
        if (investDateTo) {
          const toDate = parseDateSafe(investDateTo);
          if (toDate && entryDate > toDate) return false;
        }
      }

      // 4. Maturity Date Filter (From - To)
      if (maturityDateFrom || maturityDateTo) {
        const entryDate = parseDateSafe(entry.date_of_maturity);
        if (!entryDate) return false;
        if (maturityDateFrom) {
          const fromDate = parseDateSafe(maturityDateFrom);
          if (fromDate && entryDate < fromDate) return false;
        }
        if (maturityDateTo) {
          const toDate = parseDateSafe(maturityDateTo);
          if (toDate && entryDate > toDate) return false;
        }
      }

      return true;
    });
  }, [entries, searchQuery, bankFilter, investDateFrom, investDateTo, maturityDateFrom, maturityDateTo]);

  // Recalculated totals on filtered entries using date-only interval partitioning
  const filteredTotals = useMemo(() => {
    let total_maturity_value = 0;
    let total_interest_receivable = 0;
    let total_interest_received = 0;

    filteredEntries.forEach(entry => {
      if (entry.maturity_value) total_maturity_value += parseFloat(entry.maturity_value);
      if (entry.interest_receivable) total_interest_receivable += parseFloat(entry.interest_receivable);
      if (entry.interest_received) total_interest_received += parseFloat(entry.interest_received);
    });

    const parseDateSafe = (dateStr) => {
      if (!dateStr) return null;
      const s = String(dateStr).trim().split(' ')[0];
      if (!s) return null;

      if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(s)) {
        const parts = s.split('-');
        const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
        return isNaN(d.getTime()) ? null : d;
      }

      const match = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/);
      if (match) {
        let day = parseInt(match[1], 10);
        let month = parseInt(match[2], 10) - 1;
        let year = parseInt(match[3], 10);
        if (year < 100) {
          year += year >= 50 ? 1900 : 2000;
        }
        const d = new Date(year, month, day);
        return isNaN(d.getTime()) ? null : d;
      }

      const d = new Date(s);
      return isNaN(d.getTime()) ? null : d;
    };

    // Date-based Interval Partitioning for FD Total Calculation
    // Prevents double counting sequential renewals while fully counting overlapping investments
    let undatedTotal = 0;
    const datedItems = [];

    filteredEntries.forEach((entry, idx) => {
      const amt = entry.amount_of_fd ? parseFloat(entry.amount_of_fd) : 0;
      const invDate = parseDateSafe(entry.date_of_investment);
      const matDate = parseDateSafe(entry.date_of_maturity);

      if (!invDate || !matDate || matDate < invDate) {
        undatedTotal += amt;
      } else {
        datedItems.push({
          entry,
          invDate,
          matDate,
          amount: amt,
          id: entry.id !== undefined && entry.id !== null ? entry.id : idx,
        });
      }
    });

    // Sort chronologically by investment date, then maturity date, then id
    datedItems.sort((a, b) => {
      if (a.invDate.getTime() !== b.invDate.getTime()) {
        return a.invDate.getTime() - b.invDate.getTime();
      }
      if (a.matDate.getTime() !== b.matDate.getTime()) {
        return a.matDate.getTime() - b.matDate.getTime();
      }
      return (a.id || 0) - (b.id || 0);
    });

    const chains = [];

    datedItems.forEach(item => {
      // Find eligible chains where item.invDate >= last entry's matDate
      let bestChainIndex = -1;
      let maxLastMatTime = -1;

      for (let i = 0; i < chains.length; i++) {
        const lastEntry = chains[i][chains[i].length - 1];
        if (item.invDate.getTime() >= lastEntry.matDate.getTime()) {
          if (lastEntry.matDate.getTime() > maxLastMatTime) {
            maxLastMatTime = lastEntry.matDate.getTime();
            bestChainIndex = i;
          }
        }
      }

      if (bestChainIndex >= 0) {
        chains[bestChainIndex].push(item);
      } else {
        // Overlaps with all active chains -> create new concurrent investment chain
        chains.push([item]);
      }
    });

    // Total FD Amount is the sum of latest continuing principal of each renewal chain + undated entries
    let total_amount_of_fd = undatedTotal;
    chains.forEach(chain => {
      let chainAmt = 0;
      for (let i = chain.length - 1; i >= 0; i--) {
        if (chain[i].amount > 0) {
          chainAmt = chain[i].amount;
          break;
        }
      }
      if (chainAmt === 0 && chain.length > 0) {
        chainAmt = chain[chain.length - 1].amount || 0;
      }
      total_amount_of_fd += chainAmt;
    });

    return {
      total_amount_of_fd,
      total_maturity_value,
      total_interest_receivable,
      total_interest_received
    };
  }, [filteredEntries]);

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // -----------------------------------------------------
  // LOAD DATA
  // -----------------------------------------------------

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPFInterestEntries(financialYear);
      setEntries(data.entries || []);
      setTotals(data.totals || { total_amount_of_fd: 0, total_maturity_value: 0, total_interest_amount: 0, total_interest_receivable: 0, total_interest_received: 0, entry_count: 0 });
    } catch (error) {
      console.error('Failed to load PF interest entries:', error);
      showToast('Failed to load entries', 'error');
    } finally {
      setLoading(false);
    }
  }, [financialYear]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // -----------------------------------------------------
  // ROW ACTIONS
  // -----------------------------------------------------

  const handleAddRow = () => {
    const newEntry = {
      id: `temp-${Date.now()}`,
      isNew: true,
      ...emptyRow
    };
    setEntries([...entries, newEntry]);
    setEditingId(newEntry.id);
    setEditForm({ ...emptyRow });
  };

  const handleEditRow = (entry) => {
    setEditingId(entry.id);
    setEditForm({
      bank_name: entry.bank_name || '',
      date_of_investment: entry.date_of_investment || '',
      fd_no: entry.fd_no || '',
      amount_of_fd: entry.amount_of_fd || '',
      rate_of_interest: entry.rate_of_interest || '',
      duration: entry.duration || '',
      date_of_maturity: entry.date_of_maturity || '',
      month: entry.month || '',
      maturity_value: entry.maturity_value || '',
      remark: entry.remark || ''
    });
  };

  const handleSaveRow = async (id) => {
    try {
      const entry = entries.find(e => e.id === id);
      const isNew = entry?.isNew;

      if (isNew) {
        await createPFInterestEntry({
          financial_year: financialYear,
          ...editForm,
          amount_of_fd: editForm.amount_of_fd ? parseFloat(editForm.amount_of_fd) : null,
          rate_of_interest: editForm.rate_of_interest ? parseFloat(editForm.rate_of_interest) : null,
          maturity_value: editForm.maturity_value ? parseFloat(editForm.maturity_value) : null,
        });
        showToast('Entry added');
      } else {
        await updatePFInterestEntry(id, {
          ...editForm,
          amount_of_fd: editForm.amount_of_fd ? parseFloat(editForm.amount_of_fd) : null,
          rate_of_interest: editForm.rate_of_interest ? parseFloat(editForm.rate_of_interest) : null,
          maturity_value: editForm.maturity_value ? parseFloat(editForm.maturity_value) : null,
        });
        showToast('Entry updated');
      }
      setEditingId(null);
      setEditForm(null);
      await loadData(); // Refresh to get computed fields from backend
    } catch (error) {
      showToast('Error saving entry: ' + error.message, 'error');
    }
  };

  const handleDeleteRow = async (id) => {
    try {
      const entry = entries.find(e => e.id === id);
      if (entry?.isNew) {
        setEntries(entries.filter(e => e.id !== id));
        setEditingId(null);
        return;
      }

      await deletePFInterestEntry(id);
      showToast('Entry deleted');
      await loadData();
    } catch (error) {
      showToast('Error deleting entry: ' + error.message, 'error');
    }
  };

  const handleCancelEdit = (id) => {
    const entry = entries.find(e => e.id === id);
    if (entry?.isNew) {
      setEntries(entries.filter(e => e.id !== id));
    }
    setEditingId(null);
    setEditForm(null);
  };

  // -----------------------------------------------------
  // BULK UPLOAD
  // -----------------------------------------------------

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await bulkUploadPFInterest(file, financialYear);
      let msg = `${result.created_count} entries created`;
      if (result.updated_count > 0) {
        msg += `, ${result.updated_count} updated`;
      }
      showToast(msg);
      closeModal();
      await loadData();
    } catch (error) {
      showToast('Bulk upload failed: ' + error.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const closeModal = () => {
    setShowUploadModal(false);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // -----------------------------------------------------
  // RENDER HELPERS
  // -----------------------------------------------------

  const thClass = "px-4 py-3 text-left text-sm font-medium text-gray-700 whitespace-nowrap";
  const tdClass = "px-4 py-3 text-sm font-medium text-gray-900 border-b border-gray-200 whitespace-nowrap";

  const renderCell = (entry, field, isNumber = false) => {
    if (editingId === entry.id) {
      return (
        <input
          type={isNumber ? "number" : "text"}
          value={editForm[field] || ''}
          onChange={(e) => setEditForm({ ...editForm, [field]: e.target.value })}
          className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-100"
        />
      );
    }

    let val = entry[field];
    if (!val) return '-';

    if (field === 'date_of_investment' || field === 'date_of_maturity') {
      if (typeof val === 'string' && val.includes(' ')) {
        val = val.split(' ')[0];
      }
    }

    return val;
  };

  const formatNumber = (val) => {
    if (val === null || val === undefined || val === '') return '—';
    const num = parseFloat(val);
    if (isNaN(num)) return '—';
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatInteger = (val) => {
    if (val === null || val === undefined || val === '') return '—';
    const num = Math.round(parseFloat(val));
    if (isNaN(num)) return '—';
    return num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  if (!canView) {
    return (
      <div className="p-8 text-center text-slate-500">
        You do not have permission to view PF Interest entries.
      </div>
    );
  }

  return (
    <div className={isEmbedded ? "font-sans text-slate-900" : "min-h-screen bg-slate-50 font-sans text-slate-900 pb-12"}>
      {/* HEADER */}
      {!isEmbedded ? (
        <div className="bg-white shadow-sm border-b border-slate-200 sticky top-0 z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 -ml-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-slate-900">PF Interest Management</h1>
              </div>
              <div className="flex items-center gap-2 border-l border-slate-200 pl-4 ml-2">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Financial Year</span>
                <div className="relative">
                  <select
                    value={financialYear}
                    onChange={(e) => setFinancialYear(e.target.value)}
                    className="appearance-none rounded-lg border border-slate-200 bg-slate-50 py-1.5 pl-3 pr-8 text-sm font-semibold text-slate-700 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
                  >
                    {getFinancialYears().map((fy) => (
                      <option key={fy} value={fy}>
                        FY {fy} (April - March)
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2.5 top-2.5 h-4 w-4 text-slate-400" />
                </div>
              </div>
            </div>
            {canAdd && (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  Bulk Upload
                </button>
                <button
                  onClick={handleAddRow}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-700 hover:bg-blue-800 rounded-lg shadow-sm transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Entry
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-slate-100 pb-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:items-center gap-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900">PF Interest Management</h2>
            </div>
            <div className="flex items-center gap-2 sm:border-l sm:border-slate-200 sm:pl-4">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Financial Year</span>
              <div className="relative">
                <select
                  value={financialYear}
                  onChange={(e) => setFinancialYear(e.target.value)}
                  className="appearance-none rounded-lg border border-slate-200 bg-slate-50 py-1.5 pl-3 pr-8 text-sm font-semibold text-slate-700 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
                >
                  {getFinancialYears().map((fy) => (
                    <option key={fy} value={fy}>
                      FY {fy} (April - March)
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2.5 top-2.5 h-4 w-4 text-slate-400" />
              </div>
            </div>
          </div>
          {canAdd && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowUploadModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
              >
                <Upload className="w-4 h-4" />
                Bulk Upload
              </button>
              <button
                onClick={handleAddRow}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-700 hover:bg-blue-800 rounded-lg shadow-sm transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Entry
              </button>
            </div>
          )}
        </div>
      )}

      {/* TOAST */}
      {toast && (
        <div className={`${isEmbedded ? "fixed top-4 right-4" : "fixed top-20 right-8"} z-50 px-4 py-3 rounded-lg shadow-lg border flex items-center gap-3 animate-[fadeIn_0.3s_ease-out] ${toast.type === 'error' ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
          <AlertCircle className="w-5 h-5" />
          <p className="text-sm font-semibold">{toast.message}</p>
        </div>
      )}

      {/* FILTER PANEL */}
      <div className={isEmbedded ? "mb-6 flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          {/* General Search */}
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search bank / FD number..."
              className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
            />
          </div>

          {/* Bank Select */}
          <div className="relative min-w-[200px]">
            <select
              value={bankFilter}
              onChange={(e) => setBankFilter(e.target.value)}
              className="w-full appearance-none rounded-lg border border-slate-200 bg-slate-50 py-2 pl-3 pr-8 text-sm outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
            >
              <option value="">All Banks</option>
              {uniqueBanks.map((bank) => (
                <option key={bank} value={bank}>
                  {bank}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2.5 top-2.5 h-4 w-4 text-slate-400" />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-5 items-end">
          {/* Investment Date Filter */}
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-semibold text-slate-500 uppercase tracking-wider">Investment Date</label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={investDateFrom}
                onChange={(e) => setInvestDateFrom(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
              />
              <span className="text-slate-400 text-xs">to</span>
              <input
                type="date"
                value={investDateTo}
                onChange={(e) => setInvestDateTo(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
              />
            </div>
          </div>

          {/* Maturity Date Filter */}
          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-semibold text-slate-500 uppercase tracking-wider">Maturity Date</label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={maturityDateFrom}
                onChange={(e) => setMaturityDateFrom(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
              />
              <span className="text-slate-400 text-xs">to</span>
              <input
                type="date"
                value={maturityDateTo}
                onChange={(e) => setMaturityDateTo(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
              />
            </div>
          </div>

          {/* Reset Button */}
          <div>
            <button
              onClick={handleResetFilters}
              className="w-full rounded-lg border border-slate-200 hover:bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-600 transition"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* MAIN TABLE */}
      <div className={isEmbedded ? "" : "max-w-[100vw] px-4 sm:px-6 lg:px-8 py-8 mx-auto"}>
        <div className={isEmbedded ? "overflow-hidden" : "bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"}>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-3" />
              <p className="text-sm text-slate-500">Loading entries...</p>
            </div>
          ) : (
            <div className="overflow-auto max-h-[calc(100vh-200px)]">
              <table className="w-full border-collapse">
                <thead className="sticky top-0 z-20 bg-gray-50 border-b border-gray-200 shadow-sm">
                  <tr>
                    <th className={`${thClass} w-16 text-center`}>SR.NO</th>
                    <th className={`${thClass} min-w-[200px]`}>NAME OF THE BANK</th>
                    <th className={thClass}>DATE OF INVESTMENT</th>
                    <th className={thClass}>F.D. NO</th>
                    <th className={thClass}>AMOUNT OF F.D</th>
                    <th className={thClass}>RATE OF INTEREST (%)</th>
                    <th className={thClass}>DURATION</th>
                    <th className={thClass}>DATE OF MATURITY</th>
                    <th className={thClass}>MONTH (DAYS)</th>
                    <th className={thClass}>MATURITY VALUE</th>
                    <th className={`${thClass} bg-amber-50`}>INTEREST AMOUNT</th>
                    <th className={`${thClass} bg-amber-50`}>NO OF DAYS</th>
                    <th className={`${thClass} bg-amber-50`}>INTEREST RECEIVABLE</th>
                    <th className={`${thClass} bg-amber-50`}>INTEREST RECEIVED</th>
                    <th className={`${thClass} min-w-[150px]`}>REMARK</th>
                    <th className={`${thClass} sticky right-0 bg-gray-50 z-30 w-24 text-center shadow-[-4px_0_12px_rgba(0,0,0,0.03)]`}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {entries.length === 0 ? (
                    <tr>
                      <td colSpan={16} className="px-6 py-12 text-center text-slate-500">
                        <div className="flex flex-col items-center justify-center">
                          <FileSpreadsheet className="w-12 h-12 text-slate-300 mb-3" />
                          <p className="text-sm font-medium">No interest entries found for {fyString}</p>
                          <p className="text-xs mt-1">Click "Add Entry" or "Bulk Upload" to get started.</p>
                        </div>
                      </td>
                    </tr>
                  ) : filteredEntries.length === 0 ? (
                    <tr>
                      <td colSpan={16} className="px-6 py-12 text-center text-slate-500">
                        <div className="flex flex-col items-center justify-center">
                          <FileSpreadsheet className="w-12 h-12 text-slate-300 mb-3" />
                          <p className="text-sm font-medium">No entries found matching the filter criteria</p>
                          <button onClick={handleResetFilters} className="mt-2 text-xs font-semibold text-blue-700 hover:text-blue-800">
                            Clear Filters
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredEntries.map((entry, index) => (
                      <tr key={entry.id} className={editingId === entry.id ? 'bg-blue-50/30' : 'hover:bg-gray-50'}>
                        <td className={`${tdClass} text-center font-medium text-slate-500`}>{entry.sr_no || index + 1}</td>
                        <td className={tdClass}>{renderCell(entry, 'bank_name')}</td>
                        <td className={tdClass}>{renderCell(entry, 'date_of_investment')}</td>
                        <td className={tdClass}>{renderCell(entry, 'fd_no')}</td>
                        <td className={tdClass}>{renderCell(entry, 'amount_of_fd', true)}</td>
                        <td className={tdClass}>{renderCell(entry, 'rate_of_interest', true)}</td>
                        <td className={tdClass}>{renderCell(entry, 'duration')}</td>
                        <td className={tdClass}>{renderCell(entry, 'date_of_maturity')}</td>
                        <td className={tdClass}>{renderCell(entry, 'month')}</td>
                        <td className={tdClass}>{renderCell(entry, 'maturity_value', true)}</td>

                        {/* Backend computed columns - Display actual values */}
                        <td className={`${tdClass} text-right font-medium bg-amber-50/30`}>
                          {formatNumber(entry.interest_amount)}
                        </td>
                        <td className={`${tdClass} text-center font-medium bg-amber-50/30`}>
                          {entry.no_of_days !== null && entry.no_of_days !== undefined ? entry.no_of_days : '—'}
                        </td>
                        <td className={`${tdClass} text-right font-medium bg-amber-50/30`}>
                          {entry.interest_receivable && parseFloat(entry.interest_receivable) > 0 ? formatInteger(entry.interest_receivable) : '—'}
                        </td>
                        <td className={`${tdClass} text-right font-medium bg-amber-50/30`}>
                          {formatInteger(entry.interest_received)}
                        </td>

                        <td className={tdClass}>{renderCell(entry, 'remark')}</td>

                        {/* Actions */}
                        <td className={`${tdClass} sticky right-0 bg-white shadow-[-4px_0_12px_rgba(0,0,0,0.03)] p-0 align-middle`}>
                          <div className="flex items-center justify-center gap-1.5 h-full px-2">
                            {canAdd ? (
                              editingId === entry.id ? (
                                <>
                                  <button onClick={() => handleSaveRow(entry.id)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded" title="Save">
                                    <Save className="w-4 h-4" />
                                  </button>
                                  <button onClick={() => handleCancelEdit(entry.id)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded" title="Cancel">
                                    <X className="w-4 h-4" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button onClick={() => handleEditRow(entry)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Edit">
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button onClick={() => handleDeleteRow(entry.id)} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded" title="Delete">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </>
                              )
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}

                  {/* TOTALS ROW */}
                  {filteredEntries.length > 0 && (
                    <tr className="bg-slate-100 font-bold border-t-2 border-slate-200">
                      <td colSpan={4} className="px-4 py-4 text-right text-sm text-slate-700">TOTAL</td>
                      <td className="px-4 py-4 text-sm text-slate-900">{formatNumber(filteredTotals.total_amount_of_fd)}</td>
                      <td colSpan={4}></td>
                      <td className="px-4 py-4 text-sm text-slate-900">{formatNumber(filteredTotals.total_maturity_value)}</td>
                      <td className="px-4 py-4"></td>
                      <td className="px-4 py-4"></td>
                      <td className="px-4 py-4 text-sm text-right text-slate-900 bg-amber-50/30">
                        {filteredTotals.total_interest_receivable > 0 ? formatInteger(filteredTotals.total_interest_receivable) : '—'}
                      </td>
                      <td className="px-4 py-4 text-sm text-right text-slate-900 bg-amber-50/30">{formatInteger(filteredTotals.total_interest_received)}</td>
                      <td colSpan={2}></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* BULK UPLOAD MODAL */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={closeModal} />
          <div className="relative z-10 w-full max-w-2xl bg-white rounded-2xl shadow-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">Bulk Upload PF Interest Data</h2>
              <button onClick={closeModal} className="p-2 text-slate-400 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="border-2 border-dashed border-slate-300 rounded-xl p-12 text-center bg-slate-50 hover:bg-slate-100 transition-colors">
              {uploading ? (
                <div className="flex flex-col items-center">
                  <Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-4" />
                  <p className="text-sm font-medium text-slate-700">Uploading and processing...</p>
                  <p className="text-xs text-slate-500 mt-1">Computing Interest Amount, No of Days, and Interest Received</p>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-slate-400 mx-auto mb-4" />
                  <p className="text-sm font-medium text-slate-700 mb-1">Select your Excel file to upload</p>
                  <p className="text-xs text-slate-500 mb-6">Supports .xlsx, .xls — Computed columns will be calculated automatically</p>
                  <input
                    type="file"
                    accept=".xlsx, .xls"
                    className="hidden"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50 shadow-sm"
                  >
                    Browse Files
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
